import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  setCurrentPage, 
  setSearchTerm, 
  setSortOption, 
  selectPagination,
  selectCurrentPage,
  selectTotalPages,
  selectTotalRecords,
  selectSearchTerm,
  selectSortOption,
  selectIsLoading,
  selectError
} from '../redux/reducers/paginationSlice';

export const usePagination = (component = 'pending') => {
  const dispatch = useDispatch();
  
  // Selectors
  const pagination = useSelector(state => selectPagination(state, component));
  const currentPage = useSelector(state => selectCurrentPage(state, component));
  const totalPages = useSelector(state => selectTotalPages(state, component));
  const totalRecords = useSelector(state => selectTotalRecords(state, component));
  const searchTerm = useSelector(state => selectSearchTerm(state, component));
  const sortOption = useSelector(state => selectSortOption(state, component));
  const isLoading = useSelector(state => selectIsLoading(state, component));
  const error = useSelector(state => selectError(state, component));

  // Use refs to store stable function references
  const componentRef = useRef(component);
  componentRef.current = component;

  // Actions - simple functions without useCallback
  const goToPage = (page) => {
    console.log(`🔄 Going to page: ${page}`);
    dispatch(setCurrentPage({ page, component: componentRef.current }));
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const goToFirstPage = () => {
    goToPage(1);
  };

  const goToLastPage = () => {
    goToPage(totalPages);
  };

  const updateSearchTerm = (newSearchTerm) => {
    console.log(`🔍 Updating search term: ${newSearchTerm}`);
    dispatch(setSearchTerm({ searchTerm: newSearchTerm, component: componentRef.current }));
  };

  const updateSortOption = (newSortOption) => {
    console.log(`🔄 Updating sort option: ${newSortOption}`);
    dispatch(setSortOption({ sortOption: newSortOption, component: componentRef.current }));
  };

  // Computed values
  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;
  const pageInfo = {
    current: currentPage,
    total: totalPages,
    hasNext: hasNextPage,
    hasPrevious: hasPreviousPage,
    isFirst: isFirstPage,
    isLast: isLastPage
  };

  return {
    // State
    currentPage,
    totalPages,
    totalRecords,
    searchTerm,
    sortOption,
    isLoading,
    error,
    pageInfo,
    
    // Actions
    goToPage,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToLastPage,
    updateSearchTerm,
    updateSortOption,
    
    // Computed
    hasNextPage,
    hasPreviousPage,
    isFirstPage,
    isLastPage
  };
}; 